import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getDefaultDateRange } from '../helper';
import { DateRangeState, FilterRoute } from '@zonofi/zono-money-store';
import { useMemo } from 'react';

interface UseUrlDateRangeOptions {
  disableDefaultDateRange?: boolean;
  route?: FilterRoute;
}

export const useUrlDateRange = ({
  disableDefaultDateRange = false,
  route,
}: UseUrlDateRangeOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const getDateFromParams = (key: string) => {
    const value = searchParams.get(key);
    return value ? moment(value, 'DD-MM-YYYY').toDate() : null;
  };

  const startDateFromUrl = getDateFromParams('startDate');
  const endDateFromUrl = getDateFromParams('endDate');
  const defaultDateRangeValue = useMemo(
    () => getDefaultDateRange(route),
    [route]
  );

  const startDate = disableDefaultDateRange
    ? startDateFromUrl
    : startDateFromUrl || defaultDateRangeValue.startDate;

  const endDate = disableDefaultDateRange
    ? endDateFromUrl
    : endDateFromUrl || defaultDateRangeValue.endDate;

  const dateRangeFromUrl: DateRangeState | null =
    disableDefaultDateRange && (!startDate || !endDate)
      ? null
      : {
          key: 'selection',
          startDate: startDate!,
          endDate: endDate!,
        };

  const updateRange = (newRange: DateRangeState, targetPath?: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('startDate', moment(newRange.startDate).format('DD-MM-YYYY'));
    newParams.set('endDate', moment(newRange.endDate).format('DD-MM-YYYY'));

    const path = targetPath || location.pathname;
    navigate(`${path}?${newParams.toString()}`, { replace: true });
  };

  const resetRange = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('startDate');
    newParams.delete('endDate');
    navigate(`${location.pathname}?${newParams.toString()}`, {
      replace: true,
    });
  };

  return {
    dateRangeFromUrl, // DateRangeState | null  (for disableDefaultDateRange callers)
    appliedDateRange: dateRangeFromUrl ?? defaultDateRangeValue, // DateRangeState  (always safe)
    updateRange,
    resetRange,
  };
};
